import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Group as GroupIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { format, addWeeks, addDays, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { esFeriadoPeruano, obtenerNombreFeriado } from '../../utils/feriadosPeruanos';

const GroupRecurringCitaDialog = ({
  open,
  onClose,
  onConfirm,
  especialistas = [],
  especialidades = [],
  programas = [],
  pacientes = [],
  loading = false
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    // Datos básicos
    especialista_id: '',
    especialidad_id: '',
    programa_id: '',
    etapa_id: '',
    
    // Pacientes seleccionados
    pacientes_seleccionados: [],
    
    // Configuración de grupo
    nombre_grupo: '',
    descripcion_grupo: '',
    
    // Configuración de recurrencia
    dia_semana: '', // 'lunes', 'martes', etc.
    hora_inicio: '08:00',
    hora_fin: '09:00',
    fecha_inicio: '',
    numero_semanas: 8,
    
    // Configuración avanzada
    saltar_feriados: true,
    generar_solo_laborables: true
  });

  const [previewCitas, setPreviewCitas] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [conflictos, setConflictos] = useState([]);

  const steps = [
    'Datos Básicos',
    'Seleccionar Pacientes', 
    'Configurar Recurrencia',
    'Confirmar y Revisar'
  ];

  // Generar preview de citas cuando cambian los parámetros
  useEffect(() => {
    if (currentStep === 3 && formData.fecha_inicio && formData.dia_semana && formData.numero_semanas > 0) {
      generatePreviewCitas();
    }
  }, [currentStep, formData.fecha_inicio, formData.dia_semana, formData.numero_semanas, formData.saltar_feriados]);

  const generatePreviewCitas = () => {
    const citas = [];
    const diasSemana = {
      'lunes': 1, 'martes': 2, 'miercoles': 3, 
      'jueves': 4, 'viernes': 5, 'sabado': 6
    };
    
    const diaNumero = diasSemana[formData.dia_semana];
    if (!diaNumero) return;

    let fechaActual = new Date(formData.fecha_inicio);
    
    // Ajustar a la primera ocurrencia del día seleccionado
    while (fechaActual.getDay() !== diaNumero) {
      fechaActual = addDays(fechaActual, 1);
    }

    const conflictosEncontrados = [];

    for (let semana = 0; semana < formData.numero_semanas; semana++) {
      const fechaCita = addWeeks(fechaActual, semana);
      const fechaStr = format(fechaCita, 'yyyy-MM-dd');
      
      let incluirCita = true;
      let motivo = '';

      // Verificar si es feriado
      if (esFeriadoPeruano(fechaStr)) {
        if (formData.saltar_feriados) {
          incluirCita = false;
          motivo = `Omitida: ${obtenerNombreFeriado(fechaStr)}`;
        } else {
          conflictosEncontrados.push({
            fecha: fechaStr,
            tipo: 'feriado',
            mensaje: `${obtenerNombreFeriado(fechaStr)} - Feriado nacional`
          });
        }
      }

      // Verificar si es domingo
      if (fechaCita.getDay() === 0 && formData.generar_solo_laborables) {
        incluirCita = false;
        motivo = 'Omitida: Domingo';
      }

      citas.push({
        fecha: fechaStr,
        fechaDisplay: format(fechaCita, "EEEE d 'de' MMMM", { locale: es }),
        hora_inicio: formData.hora_inicio,
        hora_fin: formData.hora_fin,
        incluir: incluirCita,
        motivo: motivo,
        semana: semana + 1,
        totalPacientes: formData.pacientes_seleccionados.length
      });
    }

    setPreviewCitas(citas);
    setConflictos(conflictosEncontrados);
  };

  const validateCurrentStep = () => {
    const errors = {};
    
    switch (currentStep) {
      case 0: // Datos básicos
        if (!formData.especialista_id) errors.especialista = 'Seleccione un especialista';
        if (!formData.especialidad_id) errors.especialidad = 'Seleccione una especialidad';
        break;
        
      case 1: // Pacientes
        if (formData.pacientes_seleccionados.length === 0) {
          errors.pacientes = 'Seleccione al menos un paciente';
        }
        if (formData.pacientes_seleccionados.length > 10) {
          errors.pacientes = 'Máximo 10 pacientes por cita grupal';
        }
        break;
        
      case 2: // Recurrencia
        if (!formData.dia_semana) errors.dia_semana = 'Seleccione un día de la semana';
        if (!formData.fecha_inicio) errors.fecha_inicio = 'Seleccione fecha de inicio';
        if (formData.numero_semanas < 1) errors.numero_semanas = 'Mínimo 1 semana';
        if (formData.numero_semanas > 52) errors.numero_semanas = 'Máximo 52 semanas';
        
        // Validar que la fecha de inicio no sea feriado
        if (formData.fecha_inicio && esFeriadoPeruano(formData.fecha_inicio)) {
          errors.fecha_inicio = `${obtenerNombreFeriado(formData.fecha_inicio)} es feriado nacional`;
        }
        break;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handlePacienteToggle = (pacienteId) => {
    setFormData(prev => ({
      ...prev,
      pacientes_seleccionados: prev.pacientes_seleccionados.includes(pacienteId)
        ? prev.pacientes_seleccionados.filter(id => id !== pacienteId)
        : [...prev.pacientes_seleccionados, pacienteId]
    }));
  };

  const handleConfirm = () => {
    if (validateCurrentStep()) {
      const citasValidas = previewCitas.filter(cita => cita.incluir);
      
      const dataToSubmit = {
        ...formData,
        citas_programadas: citasValidas,
        total_citas: citasValidas.length,
        conflictos: conflictos
      };
      
      onConfirm(dataToSubmit);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Datos Básicos
        return (
          <Stack spacing={3}>
            <Typography variant="h6" color="primary">
              Configuración Básica de la Cita Grupal
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!validationErrors.especialista}>
                  <InputLabel>Especialista</InputLabel>
                  <Select
                    value={formData.especialista_id}
                    label="Especialista"
                    onChange={(e) => setFormData(prev => ({ ...prev, especialista_id: e.target.value }))}
                  >
                    {especialistas.map(esp => (
                      <MenuItem key={esp.id} value={esp.id}>
                        {esp.usuario?.nombre} {esp.usuario?.apellidos}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.especialista && (
                    <Typography variant="caption" color="error">
                      {validationErrors.especialista}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!validationErrors.especialidad}>
                  <InputLabel>Especialidad</InputLabel>
                  <Select
                    value={formData.especialidad_id}
                    label="Especialidad"
                    onChange={(e) => setFormData(prev => ({ ...prev, especialidad_id: e.target.value }))}
                  >
                    {especialidades.map(esp => (
                      <MenuItem key={esp.id} value={esp.id}>
                        {esp.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Programa (Opcional)</InputLabel>
                  <Select
                    value={formData.programa_id}
                    label="Programa"
                    onChange={(e) => setFormData(prev => ({ ...prev, programa_id: e.target.value }))}
                  >
                    <MenuItem value="">Sin programa específico</MenuItem>
                    {programas.map(prog => (
                      <MenuItem key={prog.id} value={prog.id}>
                        {prog.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombre del Grupo"
                  value={formData.nombre_grupo}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombre_grupo: e.target.value }))}
                  placeholder="Ej: Terapia Grupal Ansiedad - Enero 2024"
                  helperText="Nombre descriptivo para identificar el grupo"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Descripción del Grupo (Opcional)"
                  value={formData.descripcion_grupo}
                  onChange={(e) => setFormData(prev => ({ ...prev, descripcion_grupo: e.target.value }))}
                  placeholder="Descripción breve del objetivo del grupo terapéutico"
                />
              </Grid>
            </Grid>
          </Stack>
        );

      case 1: // Seleccionar Pacientes
        return (
          <Stack spacing={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" color="primary">
                Seleccionar Pacientes para el Grupo
              </Typography>
              <Chip 
                label={`${formData.pacientes_seleccionados.length} seleccionados`}
                color={formData.pacientes_seleccionados.length > 0 ? 'primary' : 'default'}
              />
            </Box>
            
            {validationErrors.pacientes && (
              <Alert severity="error">{validationErrors.pacientes}</Alert>
            )}
            
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <Grid container spacing={1}>
                {pacientes.map(paciente => (
                  <Grid item xs={12} sm={6} md={4} key={paciente.id}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        border: formData.pacientes_seleccionados.includes(paciente.id) ? 
                          '2px solid' : '1px solid',
                        borderColor: formData.pacientes_seleccionados.includes(paciente.id) ?
                          'primary.main' : 'divider',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                      onClick={() => handlePacienteToggle(paciente.id)}
                    >
                      <CardContent sx={{ py: 1 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Checkbox
                            checked={formData.pacientes_seleccionados.includes(paciente.id)}
                            size="small"
                          />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {paciente.nombres} {paciente.apellidos}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              HC: {paciente.numero_historia}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Stack>
        );

      case 2: // Configurar Recurrencia
        return (
          <Stack spacing={3}>
            <Typography variant="h6" color="primary">
              Configurar Patrón de Recurrencia
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!validationErrors.dia_semana}>
                  <InputLabel>Día de la Semana</InputLabel>
                  <Select
                    value={formData.dia_semana}
                    label="Día de la Semana"
                    onChange={(e) => setFormData(prev => ({ ...prev, dia_semana: e.target.value }))}
                  >
                    <MenuItem value="lunes">Lunes</MenuItem>
                    <MenuItem value="martes">Martes</MenuItem>
                    <MenuItem value="miercoles">Miércoles</MenuItem>
                    <MenuItem value="jueves">Jueves</MenuItem>
                    <MenuItem value="viernes">Viernes</MenuItem>
                    <MenuItem value="sabado">Sábado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Fecha de Inicio"
                  type="date"
                  value={formData.fecha_inicio}
                  onChange={(e) => setFormData(prev => ({ ...prev, fecha_inicio: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: format(new Date(), 'yyyy-MM-dd')
                  }}
                  error={!!validationErrors.fecha_inicio}
                  helperText={validationErrors.fecha_inicio}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Hora Inicio"
                  type="time"
                  value={formData.hora_inicio}
                  onChange={(e) => {
                    const [hours] = e.target.value.split(':');
                    const horaCompleta = `${hours}:00`;
                    setFormData(prev => ({ 
                      ...prev, 
                      hora_inicio: horaCompleta,
                      hora_fin: `${parseInt(hours) + 1}:00`
                    }));
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    step: 3600,
                    min: "08:00",
                    max: "19:00"
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Hora Fin"
                  type="time"
                  value={formData.hora_fin}
                  onChange={(e) => {
                    const [hours] = e.target.value.split(':');
                    setFormData(prev => ({ ...prev, hora_fin: `${hours}:00` }));
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    step: 3600,
                    min: "09:00",
                    max: "20:00"
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Número de Semanas"
                  type="number"
                  value={formData.numero_semanas}
                  onChange={(e) => setFormData(prev => ({ ...prev, numero_semanas: parseInt(e.target.value) }))}
                  inputProps={{ min: 1, max: 52 }}
                  error={!!validationErrors.numero_semanas}
                  helperText={validationErrors.numero_semanas || "Entre 1 y 52 semanas"}
                />
              </Grid>
            </Grid>
            
            <Divider />
            
            <Typography variant="subtitle1">Configuración Avanzada</Typography>
            
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.saltar_feriados}
                    onChange={(e) => setFormData(prev => ({ ...prev, saltar_feriados: e.target.checked }))}
                  />
                }
                label="Omitir feriados nacionales automáticamente"
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.generar_solo_laborables}
                    onChange={(e) => setFormData(prev => ({ ...prev, generar_solo_laborables: e.target.checked }))}
                  />
                }
                label="Generar solo en días laborables (Lun-Sáb)"
              />
            </Stack>
          </Stack>
        );

      case 3: // Confirmar y Revisar
        return (
          <Stack spacing={3}>
            <Typography variant="h6" color="primary">
              Revisar y Confirmar Citas Grupales
            </Typography>
            
            {/* Resumen de configuración */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>Resumen de Configuración</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Especialista:</Typography>
                    <Typography variant="body2">
                      {especialistas.find(e => e.id == formData.especialista_id)?.usuario?.nombre} {especialistas.find(e => e.id == formData.especialista_id)?.usuario?.apellidos}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Pacientes:</Typography>
                    <Typography variant="body2">{formData.pacientes_seleccionados.length} pacientes</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Patrón:</Typography>
                    <Typography variant="body2">
                      {formData.dia_semana} de {formData.hora_inicio} a {formData.hora_fin}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Duración:</Typography>
                    <Typography variant="body2">{formData.numero_semanas} semanas</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            
            {/* Conflictos encontrados */}
            {conflictos.length > 0 && (
              <Alert severity="warning" icon={<WarningIcon />}>
                <Typography variant="subtitle2" gutterBottom>Conflictos Encontrados:</Typography>
                {conflictos.map((conflicto, index) => (
                  <Typography key={index} variant="body2">
                    • {format(new Date(conflicto.fecha), "dd/MM/yyyy")}: {conflicto.mensaje}
                  </Typography>
                ))}
              </Alert>
            )}
            
            {/* Preview de citas */}
            <Typography variant="subtitle1">
              Citas que se crearán: {previewCitas.filter(c => c.incluir).length} de {previewCitas.length}
            </Typography>
            
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {previewCitas.map((cita, index) => (
                <Card 
                  key={index} 
                  variant="outlined" 
                  sx={{ 
                    mb: 1,
                    bgcolor: cita.incluir ? 'success.light' : 'warning.light',
                    opacity: cita.incluir ? 1 : 0.6
                  }}
                >
                  <CardContent sx={{ py: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          Semana {cita.semana}: {cita.fechaDisplay}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cita.hora_inicio} - {cita.hora_fin} | {cita.totalPacientes} pacientes
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        {cita.incluir ? (
                          <CheckIcon color="success" fontSize="small" />
                        ) : (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <WarningIcon color="warning" fontSize="small" />
                            <Typography variant="caption" color="text.secondary">
                              {cita.motivo}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { minHeight: '70vh' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <GroupIcon color="primary" />
            <Typography variant="h6">Crear Citas Grupales Recurrentes</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={currentStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        
        {currentStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Anterior
          </Button>
        )}
        
        {currentStep < steps.length - 1 ? (
          <Button 
            onClick={handleNext}
            variant="contained"
            disabled={loading}
          >
            Siguiente
          </Button>
        ) : (
          <Button 
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={loading || previewCitas.filter(c => c.incluir).length === 0}
            startIcon={loading ? <ScheduleIcon /> : <CheckIcon />}
          >
            {loading ? 'Creando...' : `Crear ${previewCitas.filter(c => c.incluir).length} Citas`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GroupRecurringCitaDialog;